import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { formatDateTime } from '~/utils/date';

import type { CostEstimate, CostScenarioTotal } from '../types/cost-estimate.types';
import { APPROVAL_MODE_LABEL_KEY } from '../utils/decision-labels';
import { formatUsd } from '../utils/format-usd';

/** Rate key of the platform's enrichment task — the step that dominates the fallback share. */
const ENRICHMENT_RATE_KEY: Record<CostEstimate['platform'], string> = {
  GMC: 'google_enrichment',
  META: 'meta_enrichment',
};

/** agent-services' calibrated `backlog_threshold_products` behind the `below` / `above` loads;
 *  the estimate does not carry it, so it is mirrored here. */
const BACKLOG_THRESHOLD_PRODUCTS = 10_000;

/** Approved Vertex only: Vertex-primary steps run with no GPT-5 fallback, so the fallback
 *  share no longer applies and the Gemini-only figure is the one to read. */
function isApprovedVertexOnly(estimate: CostEstimate): boolean {
  return estimate.status === 'APPROVED' && estimate.approvalMode === 'VERTEX_ONLY';
}

/**
 * "Expected GPT-5 fallback: N% (…)" — why the projected total sits between the Gemini-only
 * and GPT-5-only figures. The parenthesis names the rate's source: live routing (with the
 * estimate's time — the rate was measured then, not now), Vertex quota headroom, recent prod
 * runs (fitted / static / mixed), or the historical backlog wording (`historical`, or no
 * source). An unrecognised source gets no parenthesis. Nothing without a rate.
 */
export function CostFallbackHint({
  estimate,
  testId = 'cost-fallback-hint',
}: {
  estimate: CostEstimate;
  /** E2E `data-testid` for the hint line. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  const fallback = estimate.fallback;
  const rate = fallback?.rates?.[ENRICHMENT_RATE_KEY[estimate.platform]];
  if (typeof rate !== 'number') return null;
  const pct = Math.round(rate * 100);
  let text: string;
  switch (fallback?.source) {
    case 'live':
      text = t('costScenarios.fallbackHint.live', {
        pct,
        minutes: fallback.windowMinutes ?? 60,
        at: formatDateTime(estimate.createdAt),
      });
      break;
    case 'quota':
      text = t('costScenarios.fallbackHint.quota', { pct });
      break;
    case 'fitted':
    case 'static':
    case 'mixed':
      text = t('costScenarios.fallbackHint.model', { pct });
      break;
    case 'historical':
    case null:
    case undefined:
      text =
        fallback?.load === 'below' || fallback?.load === 'above'
          ? t(`costScenarios.fallbackHint.${fallback.load}`, {
              pct,
              threshold: BACKLOG_THRESHOLD_PRODUCTS.toLocaleString(),
            })
          : t('costScenarios.fallbackHint.overall', { pct });
      break;
    default:
      text = t('costScenarios.fallbackHint.unknown', { pct });
  }
  return (
    <p data-testid={testId} className="text-muted-foreground text-xs">
      {text}
    </p>
  );
}

/** The fallback hint, or — once approved Vertex only — the note that it no longer applies. */
function FallbackLine({ estimate, testId }: { estimate: CostEstimate; testId: string }) {
  const { t } = useTranslation('common');
  if (!isApprovedVertexOnly(estimate))
    return <CostFallbackHint testId={`${testId}-fallback-hint`} estimate={estimate} />;
  return (
    <p data-testid={`${testId}-vertex-only-note`} className="text-muted-foreground text-xs">
      {t('costScenarios.vertexOnlyNote')}
    </p>
  );
}

/**
 * Compact "Gemini only $X · GPT-5 only $Y" line plus the fallback hint, under the
 * headline (projected) total. Renders nothing for estimates recorded before scenarios.
 */
export function CostScenarioSummary({
  estimate,
  testId = 'cost-scenario-summary',
}: {
  estimate: CostEstimate;
  /** E2E `data-testid` root; values derive `${testId}-...`. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  const gemini = estimate.scenarios?.geminiOnly;
  const fallback = estimate.scenarios?.fallbackOnly;
  if (!gemini && !fallback) return null;
  return (
    <div data-testid={testId} className="flex flex-col gap-1">
      <p className="text-muted-foreground flex flex-wrap gap-x-2 text-xs tabular-nums">
        {gemini ? (
          <span>
            {t('costScenarios.geminiOnly')}:{' '}
            <span data-testid={`${testId}-gemini-value`} className="text-foreground font-medium">
              {formatUsd(gemini.avgUsd)}
            </span>
          </span>
        ) : null}
        {gemini && fallback ? <span aria-hidden="true">·</span> : null}
        {fallback ? (
          <span>
            {t('costScenarios.fallbackOnly')}:{' '}
            <span data-testid={`${testId}-fallback-value`} className="text-foreground font-medium">
              {formatUsd(fallback.avgUsd)}
            </span>
          </span>
        ) : null}
      </p>
      <FallbackLine testId={testId} estimate={estimate} />
    </div>
  );
}

/** Likely range cell; "—" when the scenario has none. */
function RangeCell({ scenario }: { scenario: CostScenarioTotal }) {
  if (scenario.lowUsd == null || scenario.highUsd == null) return <>—</>;
  return (
    <>
      {formatUsd(scenario.lowUsd)} – {formatUsd(scenario.highUsd)}
    </>
  );
}

/**
 * Details-page table of the three provider scenarios (projected, Gemini only, GPT-5 only)
 * with their run totals and likely ranges. Nothing for estimates recorded before scenarios.
 */
export function CostScenarioTable({
  estimate,
  testId = 'cost-scenario-table',
}: {
  estimate: CostEstimate;
  /** E2E `data-testid` root; rows are `${testId}-row-<scenario>`. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  const scenarios = estimate.scenarios;
  if (!scenarios?.geminiOnly && !scenarios?.fallbackOnly) return null;
  // Projected equals the headline total; fall back to it when the scenario entry is missing.
  const projected = scenarios.projected ?? estimate.total;
  const vertexOnly = isApprovedVertexOnly(estimate);
  const projectedRow = {
    key: 'projected',
    label: t('costScenarios.projected'),
    scenario: projected,
  };
  const geminiRow = {
    key: 'gemini',
    label: t('costScenarios.geminiOnly'),
    scenario: scenarios.geminiOnly,
  };
  const fallbackRow = {
    key: 'fallback',
    label: t('costScenarios.fallbackOnly'),
    scenario: scenarios.fallbackOnly,
  };
  // The leading (bold) row is the scenario the run will actually follow.
  const rows: { key: string; label: string; scenario: CostScenarioTotal | null | undefined }[] =
    vertexOnly ? [geminiRow, projectedRow, fallbackRow] : [projectedRow, geminiRow, fallbackRow];
  const leadKey = rows[0]?.key;
  return (
    <div data-testid={testId} className="flex flex-col gap-2">
      <h3 className="text-foreground text-xs font-semibold">{t('costScenarios.tableTitle')}</h3>
      <Table data-testid={`${testId}-table`}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('costScenarios.columns.scenario')}</TableHead>
            <TableHead className="text-right">{t('costScenarios.columns.total')}</TableHead>
            <TableHead className="text-right">{t('costScenarios.columns.range')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ key, label, scenario }) =>
            scenario ? (
              <TableRow key={key} data-testid={`${testId}-row-${key}`}>
                <TableCell className={key === leadKey ? 'font-medium' : undefined}>
                  {label}
                </TableCell>
                <TableCell
                  data-testid={`${testId}-row-${key}-total`}
                  className="text-right tabular-nums"
                >
                  {formatUsd(scenario.avgUsd)}
                </TableCell>
                <TableCell
                  data-testid={`${testId}-row-${key}-range`}
                  className="text-muted-foreground text-right tabular-nums"
                >
                  <RangeCell scenario={scenario} />
                </TableCell>
              </TableRow>
            ) : null,
          )}
        </TableBody>
      </Table>
      <FallbackLine testId={testId} estimate={estimate} />
    </div>
  );
}

/** Badge naming how an APPROVED run was approved; nothing for other statuses or older estimates. */
export function ApprovalModeBadge({
  estimate,
  testId = 'approval-mode-badge',
}: {
  estimate: CostEstimate;
  /** E2E `data-testid` for the badge. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  if (estimate.status !== 'APPROVED' || !estimate.approvalMode) return null;
  return (
    <Badge data-testid={testId} variant="outline">
      {t(APPROVAL_MODE_LABEL_KEY[estimate.approvalMode])}
    </Badge>
  );
}
